import { DeleteIcon } from '@chakra-ui/icons'
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogOverlay,
  Button,
  Flex,
  HStack,
  IconButton,
  IconButtonProps,
  Text,
  Tooltip,
  TooltipProps,
  VStack,
} from '@chakra-ui/react'
import { PopUpMessageContext } from '@hb/react'
import React, { ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react'

const defaultAlertBodyText = "Are you sure? You can't undo this action afterwards."
export const DeleteConfirmAlert = ({
  confirmActive,
  onCancel,
  onConfirm,
  body = defaultAlertBodyText,
  deleteDisabled,
  actionName = 'Delete',
  actionLoading,
  itemName,
}: {
  confirmActive: boolean
  onCancel: () => void
  onConfirm: () => void
  deleteDisabled?: boolean
  body?: string | ReactNode
  actionLoading?: boolean
  actionName?: string
  itemName: string
}) => {
  const cancelRef = useRef<HTMLButtonElement>(null)
  return (
    <AlertDialog
      isCentered
      isOpen={confirmActive}
      leastDestructiveRef={cancelRef}
      onClose={() => onCancel()}>
      <AlertDialogOverlay>
        <AlertDialogContent bg="gray.50" gap={0}>
          <AlertDialogBody px={4} py={2}>
            <VStack align="flex-start" py={1}>
              <Text fontFamily="Hero-New" fontSize="lg" fontWeight={500}>
                {actionName} {itemName}?
              </Text>
              {typeof body === 'string' ? (
                <Text fontFamily="Hero-New" fontSize="md">
                  {body}
                </Text>
              ) : (
                body
              )}
              <HStack justify="flex-end" w="100%">
                <Button size="sm" ref={cancelRef} onClick={onCancel}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  isDisabled={deleteDisabled || actionLoading}
                  isLoading={actionLoading}
                  color="red.100"
                  colorScheme="red"
                  onClick={onConfirm}>
                  {actionName}
                </Button>
              </HStack>
            </VStack>
          </AlertDialogBody>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  )
}

export const DeleteButton: React.FC<
  Partial<IconButtonProps> & {
    onDelete: (() => Promise<any>) | (() => void)
    itemName: string
    actionName?: string
    deleteDisabled?: boolean
    noConfirm?: boolean
    text?: string
    alertBody?: string | ReactNode
    tooltipProps?: Partial<TooltipProps>
  }
> = ({
  onDelete,
  itemName,
  noConfirm,
  alertBody,
  text,
  actionName = 'Delete',
  icon,
  deleteDisabled = false,
  tooltipProps,
  ...props
}) => {
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [confirmActive, setConfirmActive] = useState(false)
  const confirmTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isMounted = useRef(true)
  useEffect(() => {
    isMounted.current = true

    return () => {
      isMounted.current = false
    }
  }, [])

  const { showError } = useContext(PopUpMessageContext)

  const handleDelete = useCallback(async () => {
    if (confirmTimer.current) clearTimeout(confirmTimer.current)
    setConfirmActive(false)
    if (onDelete) {
      setDeleteLoading(true)
      try {
        await onDelete()
      } catch (err: any) {
        console.error(err)
        showError(`Error deleting ${itemName},${err?.message || 'Unknown error'}`)
        console.error(err)
      }
      if (isMounted.current) {
        setDeleteLoading(false)
      }
    } else {
      console.error('No delete function')
    }
  }, [onDelete, showError, itemName])

  return (
    <>
      {text ? (
        <Button
          variant="ghost"
          size={props.size || 'md'}
          aria-label="delete"
          _hover={{ color: 'red.600', bg: 'red.200' }}
          color="red.600"
          borderRadius="full"
          // bg='red.300'
          onClick={e => {
            e.stopPropagation()
            if (noConfirm) handleDelete()
            else setConfirmActive(true)
          }}
          isLoading={deleteLoading}
          {...props}>
          <Flex align="center" gap={1}>
            {icon ?? <DeleteIcon />}
            <Text position="relative" top="1px">
              {text}
            </Text>
          </Flex>
        </Button>
      ) : (
        <Tooltip
          hasArrow
          colorScheme="red"
          bg="red.500"
          color="white"
          textShadow="1px 1px 3px #00000055"
          placement="top"
          label={`${actionName} ${itemName}`}
          {...tooltipProps}>
          <IconButton
            variant="ghost"
            size={props.size || 'xs'}
            aria-label={actionName}
            _hover={{ color: 'red.600', bg: 'red.200' }}
            color="red.500"
            // bg='red.300'
            icon={icon ?? <DeleteIcon />}
            onClick={e => {
              e.stopPropagation()
              if (noConfirm) handleDelete()
              else setConfirmActive(true)
            }}
            isLoading={deleteLoading}
            {...props}
          />
        </Tooltip>
      )}
      <DeleteConfirmAlert
        confirmActive={confirmActive}
        onCancel={() => {
          setConfirmActive(false)
        }}
        deleteDisabled={deleteDisabled}
        actionLoading={deleteLoading}
        onConfirm={handleDelete}
        actionName={actionName}
        body={alertBody}
        itemName={itemName}
      />
    </>
  )
}
